// src/Components/Assets/AssetCard.js

import React, { useState } from 'react';
import { Dropdown, Modal, Input } from 'antd';
import { EllipsisOutlined, PlayCircleOutlined, DownloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const AssetCard = ({ asset, onDelete, onRename, onPreview }) => {
const isVideo = asset.name?.match(/\.(mp4|mov|webm)$/i);
const isPdf = asset.name?.match(/\.pdf$/i);
const isDocx = asset.name?.match(/\.docx$/i);
const isPptx = asset.name?.match(/\.pptx$/i);
const isDocument = isPdf || isDocx || isPptx;
const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
const [newAssetName, setNewAssetName] = useState('');

  const handleRename = () => {
    onRename(asset.url, newAssetName);
    setIsRenameModalVisible(false);
  };
  
  const showRenameModal = () => {
    const nameWithoutExtension = asset.name.split('.').slice(0, -1).join('.');
    setNewAssetName(nameWithoutExtension);
    setIsRenameModalVisible(true);
  };

  const menuItems = [
    {
      key: 'download',
      icon: <DownloadOutlined />,
      label: <a href={asset.url} target="_blank" rel="noopener noreferrer" download={asset.name}>Download</a>,
    },
    { key: 'rename', icon: <EditOutlined />, label: 'Rename', onClick: showRenameModal },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: 'Delete Asset',
          content: 'Are you sure you want to delete this asset?',
          okText: 'Delete',
          okType: 'danger',
          onOk: () => onDelete(asset.url),
        });
      },
    },
  ];

  return (
    <>

      <div 
        className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden shadow-sm border cursor-pointer"
        onClick={() => onPreview(asset)}
      >
        {isDocument ? (
            <div className={`w-full h-full flex flex-col items-center justify-center text-center p-3 ${
            isPdf ? 'bg-red-50' : isDocx ? 'bg-blue-50' : 'bg-orange-50'
            }`}>
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
            isPdf ? 'bg-red-100' : isDocx ? 'bg-blue-100' : 'bg-orange-100'
            }`}>
            <i className={`fa-solid ${
            isPdf ? 'fa-file-pdf' : isDocx ? 'fa-file-word' : 'fa-file-powerpoint'
            } text-2xl ${
            isPdf ? 'text-red-600' : isDocx ? 'text-blue-600' : 'text-orange-600'
            }`}></i>
            </div>
            <p className="text-xs font-bold text-gray-700 break-all line-clamp-2">{asset.name}</p>
            <p className="text-[10px] text-gray-500 mt-1 uppercase">
            {isPdf ? 'PDF Document' : isDocx ? 'Word Document' : 'PowerPoint'}
            </p>
            </div>
            ) : isVideo ? (
            <div className="relative w-full h-full bg-black">
            <video
            src={asset.url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-white/85 flex items-center justify-center shadow-md">
            <PlayCircleOutlined className="text-2xl text-gray-800" />
            </div>
            </div>
            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded">
            VIDEO
            </div>
            </div>
            ) : (
            <img src={asset.url} alt={asset.name} className="w-full h-full object-cover" />
            )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        <div className="absolute inset-0 p-2 flex flex-col justify-between">
            <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                  <button 
                      className="w-8 h-8 flex items-center justify-center bg-white/80 rounded-full hover:bg-white backdrop-blur-sm shadow"
                      onClick={(e) => e.stopPropagation()} // Stop propagation to prevent modal open
                  >
                      <EllipsisOutlined className="text-lg text-gray-700" />
                  </button>
                </Dropdown>
            </div>
            {!isDocument && (
                <p className="text-white text-xs font-medium truncate self-start opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                    {asset.name}
                </p>
            )}
        </div>
      </div>
      
      <Modal
        title="Rename Asset"
        open={isRenameModalVisible}
        onOk={handleRename}
        onCancel={() => setIsRenameModalVisible(false)}
        okText="Rename"
        destroyOnClose
      >
        <p className="text-sm text-gray-500 mb-2">Enter the new name for the file (extension will be kept).</p>
        <Input 
          value={newAssetName}
          onChange={(e) => setNewAssetName(e.target.value)}
          placeholder="Enter new asset name"
          onPressEnter={handleRename}
        />
      </Modal>
    </>
  );
};

export default AssetCard;